import { Box, CloseButton, Icon, IconButton, Paragraph, Spinner, Text } from '@zillow/constellation-10';
import { IconExternalOutline } from '@zillow/constellation-icons';
import { useState } from 'react';
import ReactImagesViewer from 'react-images-viewer';
import type { ImagesViewerAttachment } from '../../types/attachment';

export type ImagesViewerProps = {
  imageAttachments: Array<ImagesViewerAttachment>;
  selectedImageIndex: number;
  onClose: () => void;
  onPreviewAttachmentClick?: () => void;
  previewAttachmentEventId?: string;
  formatFileName?: (fileName: string) => string;
  formatFileSize?: (bytes: number) => string;
};

const ImagesViewer = ({
  imageAttachments,
  selectedImageIndex,
  onClose,
  onPreviewAttachmentClick,
  previewAttachmentEventId,
  formatFileName = (name) => name,
  formatFileSize = (bytes) => `${bytes} bytes`,
}: ImagesViewerProps) => {
  const imageIndex = selectedImageIndex === -1 ? 0 : selectedImageIndex;
  const [currentImageIndex, setCurrentImageIndex] = useState(imageIndex || 0);

  const images = imageAttachments.map(({ fileName, fileSizeBytes, previewUrl }) => {
    const isZDocPreviewUrl = previewUrl?.includes('zillowdocs');
    // zDocs API is capable of resizing images with height and width params
    // as opposed to images generated on the FE during upload
    const src = isZDocPreviewUrl ? `${previewUrl}&width=1024` : previewUrl;

    return {
      src,
      fileName,
      fileSizeBytes,
    };
  });

  const handleNext = () => {
    const nextIndex = currentImageIndex + 1;
    setCurrentImageIndex(nextIndex);
  };

  const handlePrevious = () => {
    const prevIndex = currentImageIndex - 1;
    setCurrentImageIndex(prevIndex);
  };

  const handleAttachmentPreviewClick = () => {
    if (onPreviewAttachmentClick) {
      onPreviewAttachmentClick();
    }
  };

  const currentImage = images[currentImageIndex];

  if (!images.length) return null;

  const customControls = [
    <Box
      key="header"
      css={{
        display: 'flex',
        flexDirection: 'row',
        position: 'absolute',
        top: '-1500',
        width: '100%',
        alignItems: 'center',
        // oxlint-disable-next-line constellation/no-raw-color-values
        color: 'white',
      }}
    >
      <CloseButton
        onClick={onClose}
        aria-label="close"
        onImpact
        title="Close"
        // oxlint-disable-next-line constellation/no-raw-spacing-values -- `height: 1.5em` is a single `em` length, which is relative rather than fixed, so no sizes token matches it; `width: 1.5em` is a single `em` length, which is relative rather than fixed, so no sizes token matches it
        css={{ '& svg': { height: '1.5em', width: '1.5em' } }}
      />

      {currentImage && (
        <>
          {currentImage.fileName && currentImage.fileSizeBytes && (
            <Box css={{ display: 'flex', flexDirection: 'column', margin: '0 32px' }}>
              {/* oxlint-disable-next-line constellation/no-raw-color-values */}
              <Paragraph textStyle="body-bold" css={{ color: 'white' }}>
                {formatFileName(currentImage.fileName)}
              </Paragraph>
              {/* oxlint-disable-next-line constellation/no-raw-color-values */}
              <Paragraph textStyle="body-sm" css={{ color: 'white' }}>
                {formatFileSize(currentImage.fileSizeBytes)}
              </Paragraph>
            </Box>
          )}

          {/* oxlint-disable-next-line constellation/no-raw-spacing-values -- `top: 70px` falls between `1600` (64px) and `1800` (72px) on the numeric spacing scale */}
          <Box css={{ position: 'absolute', left: '48%', top: '70px' }}>
            {/* oxlint-disable-next-line constellation/no-raw-color-values */}
            <Text textStyle="body-sm-bold" css={{ color: 'white' }}>
              {currentImageIndex + 1}
            </Text>
            {/* oxlint-disable-next-line constellation/no-raw-color-values */}
            <Text textStyle="body-sm" css={{ color: 'white', paddingLeft: 'tightest', paddingRight: 'tightest' }}>
              /
            </Text>
            {/* oxlint-disable-next-line constellation/no-raw-color-values */}
            <Text textStyle="body-sm" css={{ color: 'white' }}>
              {imageAttachments.length}
            </Text>
          </Box>

          {currentImage.src && (
            <IconButton
              asChild
              emphasis="bare"
              aria-label="open image in new tab"
              icon={<IconExternalOutline />}
              onClick={handleAttachmentPreviewClick}
              data-eventid={previewAttachmentEventId}
              onImpact={true}
              shape="circle"
              size="md"
              title="Open image in new tab"
              css={{ position: 'absolute', right: 0, marginTop: 'tighter' }}
            >
              <a href={currentImage.src} target="_blank" rel="noopener noreferrer" />
            </IconButton>
          )}
        </>
      )}
    </Box>,
  ];

  const SpinnerLoader = () => (
    <Icon>
      <Spinner size="lg" />
    </Icon>
  );

  return (
    <>
      {!!images && (
        <ReactImagesViewer
          backdropCloseable={true}
          currImg={currentImageIndex}
          customControls={customControls}
          imgs={images}
          isOpen={true}
          onClickNext={handleNext}
          onClickPrev={handlePrevious}
          onClose={onClose}
          showCloseBtn={false}
          showImgCount={false}
          spinner={SpinnerLoader}
        />
      )}
    </>
  );
};

export default ImagesViewer;
